import type { IPropertyBag } from '@deity-io/di-data-basics';
import { mapRecord } from '@deity-io/di-data-basics';
import { DefinitionLoader } from '@deity-io/di-data-structure-editor';
import { Box, Checkbox, Divider, FlexLayout, H4 } from '@deity-io/portal-ui';
import Authentication from 'components/Endpoints/EndpointSettings/Authentication';
import Configuration from 'components/Endpoints/EndpointSettings/Configuration';
import Connection from 'components/Endpoints/EndpointSettings/Connection';
import HTTPHeaders from 'components/Endpoints/EndpointSettings/HTTPHeaders';
import PaginationSettings from 'components/Endpoints/EndpointSettings/PaginationSettings';
import EntityPicker from 'components/FlowEditor/components/panels/FlowComponentSettings/EntityPicker';
import { EndpointActionTypes, EndpointContext } from 'contexts/EndpointContext';
import _ from 'lodash';
import type { FC } from 'react';
import { useContext } from 'react';
import { useGetModelQuery } from 'services/graphql/generated/hooks';
import { Variables } from './Properties';

type EndpointSettingProps = {
  isEditable: boolean;
  params: any;
};

export const EndpointSetting: FC<EndpointSettingProps> = ({ isEditable, params }) => {
  const ctx = useContext(EndpointContext);

  const { data: modelData } = useGetModelQuery({
    variables: {
      packageVersionUuid: ctx?.state.packageVersionUuid || '',
      uuid: (ctx?.state.schema.responseStructure as string) || '',
      expand: true
    },
    skip: !(ctx?.state.packageVersionUuid && ctx?.state.uuid),
    fetchPolicy: 'cache-first'
  });

  const updateConfig = (key: string[], value: any, set: boolean = true) => {
    if (ctx) {
      if (set) {
        ctx.dispatch({ type: EndpointActionTypes.SET_VALUE, payload: { key, value } });
      } else {
        ctx.dispatch({ type: EndpointActionTypes.UNSET_VALUE, payload: { key } });
      }
    }
  };

  const updateHeaders = (parentHeaders: IPropertyBag, key: string[], value: any, set: boolean = true) => {
    if (ctx) {
      if (set) {
        const filtered = mapRecord(value, (myVal, myKey) => {
          if (myKey in parentHeaders && parentHeaders[myKey] === myVal) {
            return undefined;
          }

          return myVal;
        });
        ctx.dispatch({ type: EndpointActionTypes.SET_VALUE, payload: { key, value: filtered } });
      } else {
        ctx.dispatch({ type: EndpointActionTypes.UNSET_VALUE, payload: { key } });
      }
    }
  };

  return (
    <>
      {!_.isEmpty(ctx?.state) ? (
        <Box>
          <Connection type={ctx?.state.service.schema.type} service={ctx?.state.service.name} isEditable={false} />
          <Divider />
          {ctx?.state && (
            <Configuration
              isEditable={isEditable}
              endpoint={ctx?.state.schema}
              service={ctx?.state.service.schema}
              onChange={updateConfig}
            />
          )}
          <Divider />

          <Variables
            key="variables"
            variables={ctx?.state.schema.variables}
            onChange={updateConfig}
            isEditable={isEditable}
          />

          <Divider />
          <Authentication
            authentication={ctx?.state.schema.authentication}
            parent={ctx?.state.service.schema.authentication}
            onChange={updateConfig}
            isEditable={isEditable}
          />
          <Divider />

          <HTTPHeaders
            key="headers"
            headers={ctx?.state.schema.headers || {}}
            parent={ctx?.state.service.schema.headers || {}}
            onChange={(key, value, set) => {
              updateHeaders(ctx?.state.service.schema.headers || {}, key, value, set);
            }}
            rowRenderDecorator={(key: string, value: any) =>
              ctx?.state.service.schema.headers &&
              key in ctx.state.service.schema.headers &&
              ctx.state.service.schema.headers[key] === value
                ? '(inherited)'
                : ''
            }
            isEditable={isEditable}
          />

          <Divider />
          {/* <RequestBody requestBody={ctx?.state.requestBody} onChange={updateConfig} isEditable={isEditable} /> */}
          <FlexLayout flexDirection="column" sx={{ padding: '48px' }}>
            <H4 fontWeight="semiBold">Request body structure</H4>
            <FlexLayout flexDirection="row">
              <Checkbox
                key="cb_use_resource"
                checked={ctx?.state.schema.isResourceRequest === true}
                onChange={e => {
                  updateConfig(['isResourceRequest'], true);
                }}
                label="Expect a raw resource"
              />
              <Checkbox
                key="cb_use_schema"
                checked={ctx?.state.schema.isResourceRequest !== true}
                onChange={e => {
                  updateConfig(['isResourceRequest'], false);
                }}
                label="Expect message data"
              />
            </FlexLayout>

            {ctx?.state.packageVersionUuid && ctx?.state.schema.isResourceRequest !== true && (
              <EntityPicker
                description="Request body structure"
                entity="structure"
                isEditable={isEditable}
                entityUUID={ctx.state.schema.requestStructure as string}
                onChange={(key, val) => updateConfig([key], val)}
                showEntityLink
                property="requestStructure"
              />
            )}
          </FlexLayout>
          <Divider />
          <PaginationSettings
            pagination={ctx?.state.schema.pagination}
            parent={ctx?.state.service.schema.pagination}
            // onChange={updateConfig}
            isEditable={isEditable}
            modelData={DefinitionLoader.tryLoad('responseStructure', modelData?.getModel.schema)}
          />
          <Divider />
          <FlexLayout flexDirection="column" sx={{ padding: '48px' }}>
            {/* <H4 fontWeight="semiBold">Response structure</H4> */}
            {ctx?.state.packageVersionUuid && (
              <EntityPicker
                description="Response body structure"
                entity="structure"
                isEditable={isEditable}
                entityUUID={ctx.state.schema.responseStructure as string}
                onChange={(key, val) => {
                  updateConfig([key], val);
                }}
                showEntityLink
                property="responseStructure"
              />
            )}
          </FlexLayout>
          <Divider />
          <FlexLayout flexDirection="column" sx={{ padding: '48px' }}>
            {ctx?.state.packageVersionUuid && (
              <EntityPicker
                description="Error Response body structure"
                entity="structure"
                isEditable={isEditable}
                entityUUID={ctx.state.schema.errorResponseStructure as string}
                onChange={(key, val) => {
                  updateConfig([key], val);
                }}
                showEntityLink
                property="errorResponseStructure"
              />
            )}
          </FlexLayout>
        </Box>
      ) : (
        <H4>Loading settings...</H4>
      )}
    </>
  );
};
